// @ts-strict-ignore
import { captureException, withScope } from "@sentry/browser";

import { BrazeServerSideError, FetchRequestError } from "@lib/errors";
import { getRootErrorBoundary } from "@lib/rootErrorBoundary";

import { extractResponseOrThrowBrazeServerSideError } from "./utils/extractResponseOrThrowBrazeServerSideError";
import { fetchBase } from "./fetchBase";
import type { FetchMethod, FetchOptions, RetryOptions, SettingsOptions } from "./types";

const wait = async (retryDelay: number, expBackoff: boolean, attempt: number) => {
  let delay = retryDelay;
  if (expBackoff) {
    delay = delay * 2 ** (attempt - 1);
  }

  // Add a random jitter up to 10% of the delay
  delay += delay * Math.random() * 0.1;

  await new Promise((resolve) => setTimeout(resolve, delay));
};

export const defaultRetrySettings: SettingsOptions = {
  setContentType: true,
  returnFetchRequestError: false,
  propagateFetchRequestError: true,
};

/**
 * Implements retry logic with exponential backoff.
 * Usage: fetchRetry({maxAttempts: 5, retryDelay: 1000, expBackoff: true})("http://example.com", "GET")
 *
 * @param retryOpts.maxAttempts - Number of maximum number of attempts, if attempts are
 * exhausted then the last response is return
 * @param retryOpts.retryDelay - Number in milliseconds we should wait between retries
 * if expBackoff is true we will multiple a factor of 2 ** attempt
 * @param retryOpts.expBackoff - Boolean indicating if we should perform
 * exponential backoff
 *
 * @returns A fetch compatible function with retry logic
 */
export const fetchWithRetry = (retryOpts: RetryOptions) => {
  const { maxAttempts = Infinity, retryDelay, expBackoff } = retryOpts;
  return async <R = any>(
    url: string,
    method: FetchMethod,
    params: Record<string, any> = {},
    options: FetchOptions = {},
    settings: SettingsOptions = defaultRetrySettings
  ): Promise<R> => {
    for (let attempt = 1; ; attempt++) {
      try {
        const response = await fetchBase(url, method, params, options, {
          ...settings,
          // only propagate the last error, if any
          propagateFetchRequestError: false,
        });
        if (response.status === 204) {
          return;
        }

        let text = "-";
        let json: R;
        try {
          text = await response.text();
          json = JSON.parse(text);
        } catch (jsonParseError) {
          withScope((scope) => {
            scope.setExtras({
              url,
              method,
              text,
              status: response.status,
            });
            captureException(jsonParseError);
          });

          if (attempt < maxAttempts) {
            await wait(retryDelay, expBackoff, attempt);
            continue;
          }
          throw jsonParseError;
        }
        return extractResponseOrThrowBrazeServerSideError<R>(json);
      } catch (error) {
        if (error instanceof BrazeServerSideError) {
          throw error;
        }
        if (error instanceof FetchRequestError) {
          if (error.status === 504 || error.status === 503 || error.status === 502) {
            if (attempt < maxAttempts) {
              await wait(retryDelay, expBackoff, attempt);
              continue;
            }
          }

          if (error.status === 400) {
            throw error;
          }
        }

        // because the errors thrown by fetch are Promises, they do not trigger the normal error boundary
        // unless explicitly thrown by the component that uses them. In addition, @lib/fetch can and is
        // used by legacy coffeescript code. The original $.ajax overrides for knockout/jquery display
        // a modal on server errors. This code is designed to enable that same behavior with @lib/fetch
        // but using a Beacon modal instead of a jquery UI modal.
        if (settings.propagateFetchRequestError && error instanceof FetchRequestError) {
          getRootErrorBoundary()?.renderUserModal(error);
        }
        throw error;
      }
    }
  };
};
